import type { SavedFilterStructure } from './types'

/**
 * Drop blank conditions (empty `field`) and empty groups, for comparing two
 * saved-filter structures by content. An empty builder still carries one blank
 * condition, so a raw `JSON.stringify` never equals `{ or: [] }`; pruning both
 * sides lets an emptied builder compare equal to "no filter".
 */
export const pruneBlankConditions = (
  structure: SavedFilterStructure | undefined,
): SavedFilterStructure => ({
  or: (structure?.or ?? [])
    .map((group) => ({
      and: group.and.filter((cond) => cond.field !== ''),
    }))
    .filter((group) => group.and.length > 0),
})

/**
 * List of attribute names that should be hidden in filter attribute selectors.
 * These fields will not appear as filterable options.
 */
export const HIDDEN_FILTER_ATTRIBUTES: string[] = [
  'email2',
  'email3',
  'fax',
  'phoneMobile',
  'phonePrivate',
  'pisaSalesLink',
]

export type OperatorType =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'startsWith'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'in'
  | 'nin'
  | 'empty'
  | 'notEmpty'
  | 'between'
  | 'inLast'
  | 'inNext'
  | 'notInLast'
  | 'notInNext'

export interface OperatorDefinition {
  value: OperatorType
  labelKey: string
  requiresValue: boolean
  inputType?:
    | 'text'
    | 'number'
    | 'date'
    | 'select'
    | 'multi-select'
    | 'time-range'
}

export const FIELD_TYPE_OPERATORS: Record<string, OperatorType[]> = {
  string: ['eq', 'neq', 'startsWith', 'empty', 'notEmpty'], /// , 'contains', 'endsWith'
  enum: ['eq', 'neq', 'in', 'nin', 'empty', 'notEmpty'],
  boolean: ['eq'],
  date: [
    'eq',
    'neq',
    'gte',
    'lte',
    'between',
    'inLast',
    'inNext',
    'notInLast',
    'notInNext',
    'empty',
    'notEmpty',
  ], //, 'gt', 'lt'
  datetime: [
    'eq',
    'neq',
    'gte',
    'lte',
    'between',
    'inLast',
    'inNext',
    'notInLast',
    'notInNext',
    'empty',
    'notEmpty',
  ], // alias for 'date'
  integer: ['eq', 'neq', 'gte', 'lte', 'empty', 'notEmpty'], /// , 'gt', 'lt'
  reference: ['eq', 'neq', 'in', 'nin', 'empty', 'notEmpty'], // For reference fields and _id record selection
}

export const OPERATOR_DEFINITIONS: Record<OperatorType, OperatorDefinition> = {
  eq: { value: 'eq', labelKey: 'filter.operators.eq', requiresValue: true },
  neq: { value: 'neq', labelKey: 'filter.operators.neq', requiresValue: true },
  contains: {
    value: 'contains',
    labelKey: 'filter.operators.contains',
    requiresValue: true,
  },
  startsWith: {
    value: 'startsWith',
    labelKey: 'filter.operators.startsWith',
    requiresValue: true,
  },
  gt: { value: 'gt', labelKey: 'filter.operators.gt', requiresValue: true },
  lt: { value: 'lt', labelKey: 'filter.operators.lt', requiresValue: true },
  gte: { value: 'gte', labelKey: 'filter.operators.gte', requiresValue: true },
  lte: { value: 'lte', labelKey: 'filter.operators.lte', requiresValue: true },
  in: {
    value: 'in',
    labelKey: 'filter.operators.in',
    requiresValue: true,
    inputType: 'multi-select',
  },
  nin: {
    value: 'nin',
    labelKey: 'filter.operators.nin',
    requiresValue: true,
    inputType: 'multi-select',
  },
  empty: {
    value: 'empty',
    labelKey: 'filter.operators.empty',
    requiresValue: false,
  },
  notEmpty: {
    value: 'notEmpty',
    labelKey: 'filter.operators.notEmpty',
    requiresValue: false,
  },
  between: {
    value: 'between',
    labelKey: 'filter.operators.between',
    requiresValue: true,
  },
  inLast: {
    value: 'inLast',
    labelKey: 'filter.operators.inLast',
    requiresValue: true,
    inputType: 'time-range',
  },
  inNext: {
    value: 'inNext',
    labelKey: 'filter.operators.inNext',
    requiresValue: true,
    inputType: 'time-range',
  },
  notInLast: {
    value: 'notInLast',
    labelKey: 'filter.operators.notInLast',
    requiresValue: true,
    inputType: 'time-range',
  },
  notInNext: {
    value: 'notInNext',
    labelKey: 'filter.operators.notInNext',
    requiresValue: true,
    inputType: 'time-range',
  },
}

export const isMultiValueOperator = (operator: string) =>
  operator === 'in' || operator === 'nin'

export interface EnumValueDefinition {
  title: string
  value: string
}

export const hasDuplicateEnumTitles = (enumValues?: EnumValueDefinition[]) => {
  if (!enumValues?.length) return false

  const titles = new Set<string>()
  return enumValues.some((enumValue) => {
    if (titles.has(enumValue.title)) return true
    titles.add(enumValue.title)
    return false
  })
}

export const normalizeOperatorForEnumValues = (
  operator: string,
  enumValues?: EnumValueDefinition[],
) => {
  if (!hasDuplicateEnumTitles(enumValues)) return operator
  if (operator === 'eq') return 'in'
  if (operator === 'neq') return 'nin'
  return operator
}

export const expandEnumValuesWithSameTitle = (
  value: unknown,
  enumValues?: EnumValueDefinition[],
) => {
  if (!enumValues?.length) return value

  const values = Array.isArray(value) ? value : [value]
  const expandedValues = values.flatMap((currentValue) => {
    if (typeof currentValue !== 'string') return []

    const matchingEnumValue = enumValues.find(
      (enumValue) =>
        enumValue.value === currentValue || enumValue.title === currentValue,
    )
    if (!matchingEnumValue) return [currentValue]

    return enumValues
      .filter((enumValue) => enumValue.title === matchingEnumValue.title)
      .map((enumValue) => enumValue.value)
  })

  return Array.from(new Set(expandedValues))
}
